// Conversão de erros do Supabase (PostgREST, Auth, rede) em mensagens em português.
// As funções do banco já devolvem `code` estável e `message` pronta para a interface
// (ver private.raise_error nas migrações).

export class AppError extends Error {
  readonly code: string
  constructor(code: string, message: string) {
    super(message)
    this.name = 'AppError'
    this.code = code
  }
}

const AUTH_MESSAGES: Record<string, string> = {
  otp_expired: 'O código expirou ou já foi usado. Peça um novo código.',
  invalid_credentials: 'Código inválido. Confira o código recebido por e-mail.',
  over_email_send_rate_limit: 'Muitos e-mails enviados em pouco tempo. Aguarde alguns minutos e tente de novo.',
  over_request_rate_limit: 'Muitas tentativas em pouco tempo. Aguarde alguns minutos e tente de novo.',
  over_sms_send_rate_limit: 'Muitas tentativas em pouco tempo. Aguarde alguns minutos e tente de novo.',
  captcha_failed: 'Não foi possível confirmar que você é uma pessoa. Recarregue a página e tente de novo.',
  email_address_invalid: 'Este endereço de e-mail não é válido.',
  email_address_not_authorized: 'O envio de e-mails ainda não está liberado para este endereço. Tente mais tarde.',
  signup_disabled: 'Novos cadastros estão temporariamente fechados.',
  user_banned: 'Esta conta está bloqueada.',
  validation_failed: 'Confira os dados informados.',
  session_expired: 'Sua sessão expirou. Entre novamente.',
  session_not_found: 'Sua sessão expirou. Entre novamente.',
  refresh_token_not_found: 'Sua sessão expirou. Entre novamente.',
  otp_disabled: 'Não foi possível enviar o código para este e-mail. Confira o endereço e tente de novo.',
  email_not_confirmed: 'Confirme o seu e-mail com o código recebido antes de continuar.',
  user_not_found: 'Não encontramos uma conta com este e-mail.',
  email_provider_disabled: 'O acesso por e-mail está temporariamente indisponível.',
}

const NETWORK_MESSAGE = 'Não foi possível conectar. Verifique sua internet e tente de novo.'
const GENERIC_MESSAGE = 'Algo deu errado. Tente de novo em instantes.'
const AUTH_GENERIC_MESSAGE = 'Não foi possível concluir o acesso. Confira os dados e tente de novo em instantes.'

type ErrorLike = { code?: unknown; message?: unknown; status?: unknown; name?: unknown; __isAuthError?: unknown }

function isAuthError(e: ErrorLike): boolean {
  return e.__isAuthError === true || (typeof e.name === 'string' && e.name.startsWith('Auth'))
}

export function toAppError(error: unknown): AppError {
  if (error instanceof AppError) return error
  if (error instanceof TypeError && /fetch|network|load failed/i.test(error.message)) {
    return new AppError('network', NETWORK_MESSAGE)
  }
  if (error && typeof error === 'object') {
    const e = error as ErrorLike
    const code = typeof e.code === 'string' ? e.code : ''
    const message = typeof e.message === 'string' ? e.message : ''
    if (code && AUTH_MESSAGES[code]) return new AppError(code, AUTH_MESSAGES[code])
    if (/failed to fetch|networkerror|load failed/i.test(message)) {
      return new AppError('network', NETWORK_MESSAGE)
    }
    // Erros do Supabase Auth vêm em inglês: só os códigos mapeados acima têm texto próprio.
    if (isAuthError(e)) return new AppError(code || 'auth_error', AUTH_GENERIC_MESSAGE)
    // Erros das nossas funções: código em minúsculas_com_sublinhado e mensagem em português.
    if (/^[a-z][a-z_]+$/.test(code) && message) return new AppError(code, message)
    if (code === 'PGRST301' || code === 'PGRST303' || e.status === 401) {
      return new AppError('not_authenticated', 'Sua sessão expirou. Entre novamente.')
    }
    if (code === '42501') {
      return new AppError('forbidden', 'Você não tem permissão para esta ação.')
    }
    if (code === 'PGRST116') {
      return new AppError('not_found', 'Conteúdo não encontrado.')
    }
  }
  return new AppError('unknown', GENERIC_MESSAGE)
}

export function errorMessage(error: unknown): string {
  return toAppError(error).message
}

export function errorCode(error: unknown): string {
  return toAppError(error).code
}

/** Lança o erro do Supabase (já convertido) ou devolve os dados. */
export function unwrap<T>(result: { data: T; error: unknown }): T {
  if (result.error) throw toAppError(result.error)
  return result.data
}
